import React from 'react';
import { Box, Collapse, Stack, Typography } from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { alpha } from '@mui/material/styles';
import colors from '../../assets/theme/colors';

const normalizeText = (value = '') => String(value || '').trim();

export const getTimelineCareDetailText = (draft = {}) => {
  const title = normalizeText(draft.title);
  const content = normalizeText(draft.content || draft.summary);
  const sourceText = normalizeText(draft.sourceText);
  const observationOnly = draft.durability === 'one_time_observation' || draft.promotionDecision?.action === 'observe_pattern';
  if (observationOnly) return { knowledge: sourceText || 'Original wording unavailable.', observationOnly };
  const knowledge = content && content !== sourceText ? content : title;

  return {
    knowledge,
  };
};

const TimelineCareDetails = ({
  groupedDrafts = [],
}) => {
  const [expanded, setExpanded] = React.useState(false);
  const contentId = React.useId();
  const seen = new Set();
  const visibleDetails = groupedDrafts.flatMap((group) => group.drafts.map((draft) => ({
    group, draft, ...getTimelineCareDetailText(draft),
  }))).filter(({ group, knowledge, observationOnly }) => {
    const key = JSON.stringify([group.knowledgeType, group.label, knowledge, Boolean(observationOnly)]);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  const sectionLabel = `Care details (${visibleDetails.length})`;

  return (
    <Box sx={{ py: 0.8, borderTop: `1px solid ${alpha(colors.semantic.ink, 0.08)}` }}>
      <Box
        component="button"
        type="button"
        aria-expanded={expanded}
        aria-controls={contentId}
        onClick={() => setExpanded((current) => !current)}
        sx={{
          width: '100%',
          p: 0,
          border: 0,
          bgcolor: 'transparent',
          color: colors.semantic.ink,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          textAlign: 'left',
        }}
      >
        <Typography component="span" sx={{ fontSize: '0.82rem', fontWeight: 950 }}>
          {sectionLabel}
        </Typography>
        <KeyboardArrowDownRoundedIcon
          aria-hidden="true"
          sx={{
            fontSize: '1.25rem',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 160ms ease',
          }}
        />
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Stack
          id={contentId}
          role="region"
          aria-label={sectionLabel}
          spacing={1.1}
          sx={{ pt: 0.75 }}
        >
          {visibleDetails.map(({ group, draft, knowledge, observationOnly }, index) => {

            return (
              <Box key={draft.clientId || draft.id || `${group.knowledgeType}-${index}`}>
                <Typography sx={{ fontSize: '0.76rem', fontWeight: 900, color: alpha(colors.semantic.ink, 0.68) }}>
                  {group.label}
                </Typography>
                <Typography sx={{ mt: 0.15, fontSize: '0.9rem', fontWeight: 900, color: colors.semantic.ink, lineHeight: 1.3 }}>
                  {knowledge}
                </Typography>
                {observationOnly && (
                  <Typography sx={{ fontSize: '0.76rem', color: colors.semantic.ink }}>
                    Observed in this entry
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      </Collapse>
    </Box>
  );
};

export default TimelineCareDetails;
